import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { UserPreferenceKeys } from "../userPreferences/userPreferencesSlice";

import { NavigationStatus, type Poi, type Position } from "../../libs/models";
import { getInitialValue } from "../userPreferences/initialValues";
import type {
  PendingNavigationRequest,
  Route,
  RouteIndication,
  RouteSegment,
  RouteStep,
  SliceProps,
} from "./types";

const initialState: SliceProps = {
  destinationPosition: null,
  errors: [],
  lastNavigationTo: null,
  navigationFrom: null,
  navigationName: undefined,
  navigationStatus: NavigationStatus.STOPPED,
  navigationTo: null,
  nearestDestinationNode: null,
  nearestOriginNode: null,
  pendingNavigationRequest: null,
  route: null,
  speakAloudIndications: getInitialValue(
    UserPreferenceKeys.SPEAK_ALOUD_INDICATIONS,
    false,
  ),
  tags: [],
};

export const NavigationSlice = createSlice({
  initialState,
  name: "routeCalculator",
  reducers: {
    clearNavigation: (state: SliceProps, _action: PayloadAction<void>) => {
      state.route = initialState.route;
      state.navigationFrom = initialState.navigationFrom;
      state.navigationTo = initialState.navigationTo;
      state.destinationPosition = initialState.destinationPosition;
      state.nearestOriginNode = initialState.nearestOriginNode;
      state.nearestDestinationNode = initialState.nearestDestinationNode;
      state.errors = initialState.errors;
      state.navigationStatus = NavigationStatus.STOPPED;
      state.tags = [];
      state.navigationName = undefined;
      state.pendingNavigationRequest = initialState.pendingNavigationRequest;
    },
    resetNavigation: (state: SliceProps, _action: PayloadAction<void>) => {
      state = initialState; // eslint-disable-line @typescript-eslint/no-unused-vars
    },
    resetNavigationEndpoints: (
      state: SliceProps,
      _action: PayloadAction<void>,
    ) => {
      state.navigationTo = initialState.navigationTo;
      state.navigationFrom = initialState.navigationFrom;
      state.destinationPosition = initialState.destinationPosition;
    },
    resetNavigationRoute: (state: SliceProps, _action: PayloadAction<void>) => {
      state.route = initialState.route;
      state.errors = initialState.errors;
      state.tags = [];
      state.navigationName = undefined;
    },
    setCurrentIndication: (
      state: SliceProps,
      action: PayloadAction<RouteIndication>,
    ) => {
      if (state.route) state.route.currentIndication = action.payload;
    },
    setCurrentStep: (state: SliceProps, action: PayloadAction<RouteStep>) => {
      if (state.route) {
        state.route.currentStep = action.payload;
      }
    },
    setDestinationPosition: (
      state: SliceProps,
      action: PayloadAction<Position | null>,
    ) => {
      state.destinationPosition = action.payload;
    },
    setDistanceToGoal: (state: SliceProps, action: PayloadAction<number>) => {
      if (state.route?.currentStep)
        state.route.currentStep.distanceToGoal = action.payload;
    },
    setNavigationErrors: (state: SliceProps, action: PayloadAction<any[]>) => {
      state.errors = action.payload;
      state.pendingNavigationRequest = initialState.pendingNavigationRequest;
    },
    setNavigationFrom: (
      state: SliceProps,
      action: PayloadAction<Poi["id"] | null>,
    ) => {
      state.navigationFrom = action.payload;
    },
    setNavigationName: (
      state: SliceProps,
      action: PayloadAction<SliceProps["navigationName"]>,
    ) => {
      state.navigationName = action.payload;
    },
    setNavigationRoute: (state: SliceProps, action: PayloadAction<Route>) => {
      // Set the current navigation route.
      state.route = action.payload;
      // Remove errors
      state.errors = initialState.errors;
      state.pendingNavigationRequest = initialState.pendingNavigationRequest;
      if (action.payload?.steps) {
        state.route.currentStep = action.payload.steps[0];
      }
    },
    setNavigationStatus: (
      state: SliceProps,
      action: PayloadAction<SliceProps["navigationStatus"]>,
    ) => {
      state.navigationStatus = action.payload;
    },
    setNavigationTo: (
      state: SliceProps,
      action: PayloadAction<Poi["id"] | null>,
    ) => {
      state.navigationTo = action.payload;
      if (action.payload) {
        state.lastNavigationTo = action.payload;
      }
    },
    setNextIndication: (
      state: SliceProps,
      action: PayloadAction<RouteIndication>,
    ) => {
      if (state.route) state.route.nextIndication = action.payload;
    },
    setPendingNavigationRequest: (
      state: SliceProps,
      action: PayloadAction<PendingNavigationRequest | null>,
    ) => {
      state.pendingNavigationRequest = action.payload;
    },
    setSpeakAloudIndications: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.speakAloudIndications = action.payload;
    },
    swapNavigationParams: (state: SliceProps, _action: PayloadAction<void>) => {
      const to = state.navigationTo;
      state.navigationTo = state.navigationFrom;
      state.navigationFrom = to;
      state.route = null;
    },
    updateNavigationRouteTags: (
      state: SliceProps,
      action: PayloadAction<SliceProps["tags"]>,
    ) => {
      state.tags = action.payload;
    },
    updateSegments: (
      state: SliceProps,
      action: PayloadAction<RouteSegment[]>,
    ) => {
      if (state.route) state.route.segments = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  resetNavigationEndpoints,
  clearNavigation,
  resetNavigationRoute,
  setNavigationFrom,
  setNavigationRoute,
  setNavigationTo,
  swapNavigationParams,
  setCurrentStep,
  setDistanceToGoal,
  resetNavigation,
  setNavigationErrors,
  updateSegments,
  setNavigationStatus,
  updateNavigationRouteTags,
  setNavigationName,
  setCurrentIndication,
  setNextIndication,
  setSpeakAloudIndications,
  setDestinationPosition,
  setPendingNavigationRequest,
} = NavigationSlice.actions;

export default NavigationSlice.reducer;
